import React, { useEffect, useRef, useState } from 'react';
import { parseMarkdown } from './utils';
import Button from '../../components/common/Button.js';
import Typography from '../../components/common/Typography';

import questionerIcon from '../../asset/img/ai_assistant_icon_02.svg';
import answererIcon from '../../asset/img/ai_assistant_icon_03.svg';
import sample01 from '../../asset/img/ai_assistant_sample_01.png';
import callSmallIcon from '../../asset/img/ai_assistant_call_small_icon.svg';
import callCenterIcon from '../../asset/img/ai_assistant_callcenter_icon.svg';

const COLLAPSED_HEIGHT = 80; // 1줄 높이 기준

export const QuestionBox = ({ text }) => (
  <div className="aiQuestionBox">
    <div className={'contentBox'}>
      <Typography {...{ variant: 'body2Medium' }}>{text}</Typography>
    </div>
    <img src={questionerIcon} alt={'questionerIcon'} />
  </div>
);

export const AnswerBox = ({ text, flags, buttons, resFlag }) => {
  const btnBoxRef = useRef(null);
  const [hasMultipleRows, setHasMultipleRows] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [fullHeight, setFullHeight] = useState(0);
  const [collapsedHeight, setCollapsedHeight] = useState(COLLAPSED_HEIGHT);

  // 버튼 영역이 2줄 이상인지 확인
  useEffect(() => {
    if (btnBoxRef.current && buttons && buttons.length > 0) {
      const container = btnBoxRef.current;
      const fullHeightValue = container.scrollHeight;
      setFullHeight(fullHeightValue);

      // 2줄 이상인지 확인 (COLLAPSED_HEIGHT 보다 크면 2줄 이상)
      if (fullHeightValue > COLLAPSED_HEIGHT) {
        setHasMultipleRows(true);
        setCollapsedHeight(COLLAPSED_HEIGHT);
      } else {
        setHasMultipleRows(false);
      }
    }
  }, [buttons]);

  return (
    <div className="aiAnswerBox">
      <div className={'displayFlex'} style={{ gap: '8px', alignItems: 'start' }}>
        <img
          src={answererIcon}
          alt={'answererIcon'}
          className={resFlag ? 'aiResponding' : ''}
          style={{ width: '36px', height: '36px' }}
        />
        <div className={'contentBox noneScroll'}>
          <div className={'body2Medium contentPieces'} style={{ whiteSpace: 'pre-wrap' }}>
            {parseMarkdown(text)}
          </div>
        </div>
      </div>
      {/* TODO: 현재는 결과조회에서만 샘플이미지 띄우도록 되어있음. 추후 추가 시 로직 변경 필요 */}
      {flags && flags.includes(4) && (
        <div className={'sampleImgBox'}>
          <img src={sample01} alt={'sampleImg'} />
        </div>
      )}
      {buttons && buttons.length > 0 && (
        <div
          ref={btnBoxRef}
          className={`answerBtnBox ${hasMultipleRows ? 'hasToggle' : ''}`}
          style={{
            maxHeight: hasMultipleRows && !isExpanded ? `${collapsedHeight}px` : `${fullHeight}px`,
            overflow: 'hidden',
            transition: 'max-height 0.3s ease-in-out',
          }}>
          {React.Children.map(buttons, (button, index) => (
            <React.Fragment key={`answer-btn-${index}`}>{button}</React.Fragment>
          ))}
        </div>
      )}
      {hasMultipleRows && (
        <div className="answerBtnToggle" onClick={() => setIsExpanded(!isExpanded)}>
          <span>{isExpanded ? '접기 ▲' : '더보기 ▼'}</span>
        </div>
      )}
    </div>
  );
};

export const LoadingBox = ({ systemMsg }) => (
  <div className="aiLoadingBox">
    <img src={answererIcon} alt={'answererIcon'} />
    <div className="skeletonBox">
      <div className="skeletonContent">
        <Typography
          {...{ variant: 'body2Medium', color: 'GrayScale-600', style: { width: '100%' } }}>
          {systemMsg}
        </Typography>
      </div>
    </div>
  </div>
);

export const FailBox = ({
  text,
  hasChatList,
  handleReset,
  handleRetry,
  openGitple,
  handleCallCenter,
  callcenter,
  isChatAvailable,
}) => (
  <>
    <div className="aiFailBox">
      <div className={'displayFlex'} style={{ gap: '8px', alignItems: 'start' }}>
        <img src={answererIcon} alt={'answererIcon'} style={{ width: '36px', height: '36px' }} />
        <div className={'contentBox'}>
          <Typography {...{ variant: 'body2Medium', color: 'Error-400' }}>{text}</Typography>
        </div>
      </div>
      {isChatAvailable && (
        <div className={'answerBtnBox'}>
          <AnswerButton
            {...{
              onClick: openGitple,
              text: '채팅상담',
              icon: callSmallIcon,
            }}
          />
          {callcenter && (
            <AnswerButton
              {...{
                onClick: handleCallCenter,
                text: '전화상담',
                icon: callCenterIcon,
              }}
            />
          )}
        </div>
      )}
    </div>
    <div className={'displayFlexBetween'} style={{ gap: '8px', marginTop: '10px' }}>
      <Button
        {...{
          onClick: handleReset,
          value: '대화 새로 시작',
          style: { width: '100%' },
          color: 'secondary',
        }}></Button>
      {/* 대화 목록이 있는 경우에만 재시도 버튼 표시 */}
      {hasChatList && (
        <Button
          {...{
            onClick: handleRetry,
            value: '재시도',
            style: { width: '100%' },
          }}></Button>
      )}
    </div>
  </>
);

/* AnswerBox의 키워드에 따라 생성되는 버튼 컴포넌트 */
export const AnswerButton = ({ onClick, text, icon, btnStyle, textStyle }) => {
  return (
    <div className={'answerBtn'} style={btnStyle} onClick={onClick}>
      <div className={'answerBtnImgBox'}>
        <img src={icon} alt={`${text}Icon`} />
      </div>
      <p style={textStyle}>{text}</p>
    </div>
  );
};
